"use client";

import { useEffect, useRef } from "react";

type UseInfiniteScrollOptions = {
  /** Whether the observer should be active. */
  enabled?: boolean;
  /** Margin around the root used to trigger the callback earlier. */
  rootMargin?: string;
};

/**
 * Calls `onReachEnd` whenever the returned sentinel element scrolls into view.
 *
 * Attach the returned ref to an element rendered at the bottom of a list to
 * drive infinite scroll. The latest `onReachEnd` is always used, so callers do
 * not need to memoize it, and the observer is recreated only when `enabled` or
 * `rootMargin` change.
 */
export function useInfiniteScroll<T extends HTMLElement = HTMLDivElement>(
  onReachEnd: () => void,
  { enabled = true, rootMargin = "200px" }: UseInfiniteScrollOptions = {}
) {
  const sentinelRef = useRef<T | null>(null);
  const callbackRef = useRef(onReachEnd);
  callbackRef.current = onReachEnd;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !enabled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          callbackRef.current();
        }
      },
      { rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, rootMargin]);

  return sentinelRef;
}
